"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useRouter } from "next/navigation";

import { CornerDownLeft, FileText, Loader2, Search, X } from "lucide-react";

import { MODULOS } from "@/lib/modulos";
import { cn } from "@/lib/utils";

/**
 * El buscador del Centro de ayuda: `⌘K` / `Ctrl+K`, modal, resultados agrupados por módulo con el
 * término resaltado y navegación con teclado.
 *
 * 🔑 **Busca en el navegador, contra un índice que se pide una sola vez.** El índice
 * (`GET /api/busqueda`) son los 107 artículos en texto corrido: **181 KB comprimidos**, medidos. Se
 * pide **al abrir el buscador por primera vez**, no al cargar la página, así que quien nunca busca no
 * paga nada. A cambio, a partir de ahí escribir no cuesta ni una petición.
 *
 * ⚠️ **Sin Algolia ni ningún servicio externo, al revés que la referencia.** Con 107 artículos el
 * navegador los recorre en microsegundos, y **lo que busca cada cliente no se le manda a un tercero**.
 *
 * ⚠️ **La comparación va sin tildes y en minúsculas, en los dos lados.** Es castellano: quien busca
 * «facturacion» tiene que encontrar «facturación», y quien busca «NÓMINA» lo mismo. Sin normalizar,
 * el buscador falla justo en las palabras del dominio.
 */

/**
 * 🔑 **Hay un solo modal en toda la página, y se abre por evento.** La portada quiere un buscador
 * grande y la cabecera uno pequeño; montar dos `<Buscador>` daría **dos modales y dos atajos `⌘K`
 * compitiendo**, que se cancelarían entre sí. Así que el modal vive una sola vez —en la cabecera— y
 * cualquiera lo abre con `abrirBuscador()`.
 */
const EVENTO_ABRIR = "vecipp:abrir-buscador";

export function abrirBuscador() {
  window.dispatchEvent(new Event(EVENTO_ABRIR));
}

type Entrada = { m: string; s: string; t: string; r: string; c: string };
type Resultado = { entrada: Entrada; fragmento: string; puntaje: number };

const NOMBRE_MODULO = new Map(MODULOS.map((m) => [m.id, m.nombre]));

/** Minúsculas y sin diacríticos, para comparar. */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Un fragmento del cuerpo alrededor de la primera coincidencia.
 *
 * ⚠️ **Se busca sobre el texto normalizado pero se recorta sobre el original**, para que el fragmento
 * salga con sus tildes. Por eso `normalizar()` **no puede cambiar la longitud**: si quitara espacios o
 * signos, los índices dejarían de corresponder y el recorte saldría movido. Quitar diacríticos con
 * `NFD` + borrar los combinantes sí conserva la longitud del texto ya compuesto.
 */
function fragmentoDe(cuerpo: string, cuerpoNorm: string, consulta: string): string {
  const i = cuerpoNorm.indexOf(consulta);
  if (i < 0) return cuerpo.slice(0, 120);
  const desde = Math.max(0, i - 45);
  const hasta = Math.min(cuerpo.length, i + consulta.length + 75);
  return (desde > 0 ? "…" : "") + cuerpo.slice(desde, hasta).trim() + (hasta < cuerpo.length ? "…" : "");
}

/** Parte un texto por las apariciones del término, para poder resaltarlas sin `dangerouslySetInnerHTML`. */
function Resaltado({ texto, consulta }: { texto: string; consulta: string }) {
  const norm = normalizar(texto);
  const trozos: { t: string; marcado: boolean }[] = [];
  let desde = 0;
  for (;;) {
    const i = norm.indexOf(consulta, desde);
    if (i < 0 || !consulta) break;
    if (i > desde) trozos.push({ t: texto.slice(desde, i), marcado: false });
    trozos.push({ t: texto.slice(i, i + consulta.length), marcado: true });
    desde = i + consulta.length;
  }
  trozos.push({ t: texto.slice(desde), marcado: false });
  return (
    <>
      {trozos.map((trozo, i) =>
        trozo.marcado ? (
          <mark key={i} className="rounded bg-primary/15 px-0.5 text-primary">
            {trozo.t}
          </mark>
        ) : (
          <span key={i}>{trozo.t}</span>
        ),
      )}
    </>
  );
}

export function Buscador() {
  const router = useRouter();
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState("");
  const [indice, setIndice] = useState<Entrada[] | null>(null);
  const [cargando, setCargando] = useState(false);
  const [activo, setActivo] = useState(0);
  const entrada = useRef<HTMLInputElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  /* Para devolver el foco a donde estaba al cerrar — si no, se pierde al principio de la página. */
  const previo = useRef<HTMLElement | null>(null);

  const abrir = useCallback(() => {
    previo.current = document.activeElement as HTMLElement | null;
    setAbierto(true);
  }, []);

  const cerrar = useCallback(() => {
    setAbierto(false);
    setConsulta("");
    setActivo(0);
    previo.current?.focus();
  }, []);

  /* El atajo global. `⌘K` en Mac, `Ctrl+K` en el resto — el mismo que usan todos los buscadores de
     documentación, así que no hay que enseñarlo. */
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAbierto((a) => {
          if (!a) previo.current = document.activeElement as HTMLElement | null;
          return !a;
        });
      }
    };
    const alPedir = () => {
      previo.current = document.activeElement as HTMLElement | null;
      setAbierto(true);
    };
    window.addEventListener("keydown", alPulsar);
    window.addEventListener(EVENTO_ABRIR, alPedir);
    return () => {
      window.removeEventListener("keydown", alPulsar);
      window.removeEventListener(EVENTO_ABRIR, alPedir);
    };
  }, []);

  /* El índice se pide al abrir por primera vez, nunca antes. */
  useEffect(() => {
    if (!abierto || indice || cargando) return;
    setCargando(true);
    fetch("/api/busqueda")
      .then((r) => r.json())
      .then((d: { indice: Entrada[] }) => setIndice(d.indice))
      .catch(() => setIndice([]))
      .finally(() => setCargando(false));
  }, [abierto, indice, cargando]);

  useEffect(() => {
    if (abierto) entrada.current?.focus();
  }, [abierto]);

  /* Con el modal abierto la página de atrás no se desplaza: es lo que se espera de un modal, y evita
     que al llegar al final de los resultados se ponga a bajar el artículo. */
  useEffect(() => {
    if (!abierto) return;
    const previoOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previoOverflow;
    };
  }, [abierto]);

  const resultados = useMemo<Resultado[]>(() => {
    const q = normalizar(consulta.trim());
    if (!indice || q.length < 2) return [];
    const encontrados: Resultado[] = [];
    for (const e of indice) {
      const tituloNorm = normalizar(e.t);
      const resumenNorm = normalizar(e.r);
      const cuerpoNorm = normalizar(e.c);
      /* El título pesa mucho más que el cuerpo: quien escribe «turno» quiere el artículo que se llama
         así, no los doce que lo mencionan de pasada. */
      let puntaje = 0;
      if (tituloNorm.includes(q)) puntaje += tituloNorm.startsWith(q) ? 120 : 80;
      if (resumenNorm.includes(q)) puntaje += 30;
      if (cuerpoNorm.includes(q)) puntaje += 10;
      if (!puntaje) continue;
      encontrados.push({
        entrada: e,
        fragmento: resumenNorm.includes(q) ? e.r : fragmentoDe(e.c, cuerpoNorm, q),
        puntaje,
      });
    }
    return encontrados.sort((a, b) => b.puntaje - a.puntaje).slice(0, 30);
  }, [indice, consulta]);

  useEffect(() => setActivo(0), [consulta]);

  const ir = useCallback(
    (r: Resultado) => {
      cerrar();
      router.push(`/${r.entrada.m}/${r.entrada.s}`);
    },
    [cerrar, router],
  );

  const alNavegar = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cerrar();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!resultados.length) return;
      const paso = e.key === "ArrowDown" ? 1 : -1;
      setActivo((i) => {
        const siguiente = (i + paso + resultados.length) % resultados.length;
        lista.current?.querySelectorAll("[data-resultado]")[siguiente]?.scrollIntoView({ block: "nearest" });
        return siguiente;
      });
      return;
    }
    if (e.key === "Enter" && resultados[activo]) {
      e.preventDefault();
      ir(resultados[activo]);
    }
  };

  const q = normalizar(consulta.trim());

  return (
    <>
      {/* El disparador, **como el buscador de la barra de POS y Contabilidad** (2026-10-04, *«hazlo transparente y pegado
          al logo así como lo tenemos en el sistema de negocio»*): sin borde ni fondo, la lupa de 18 px con trazo fino y
          el texto en gris de 14,5 px (`Vecipp-Core/components/dashboard/header/search-dialog.tsx`). En el celular, solo la
          lupa en un círculo. El atajo ⌘K sigue funcionando aunque ya no se muestre. */}
      <button
        type="button"
        onClick={abrir}
        aria-label="Buscar en las guías"
        className="flex size-9 shrink-0 items-center justify-center rounded-full text-left text-[0.9063rem] text-muted-foreground transition-colors hover:bg-foreground/10 sm:h-[38px] sm:w-auto sm:max-w-[340px] sm:shrink sm:justify-start sm:gap-2.5 sm:rounded-none sm:hover:bg-transparent sm:hover:text-foreground"
      >
        <Search className="size-[18px] shrink-0 stroke-[1.5]" />
        <span className="hidden truncate sm:inline">Buscar en las guías…</span>
      </button>

      {/* 🔑 **El modal sale por un portal a `<body>`, y eso NO es opcional.** Este componente vive
          dentro de la cabecera, y la cabecera lleva `.bg-hero`: esa clase, además del color, arrastra
          **el remapeo entero de tokens** —incluido `--background: transparent`, que está ahí para que
          los botones de variante clara no salgan blancos sobre blanco—. Renderizado ahí dentro, el
          `bg-background` del panel del modal **se volvía transparente**: es lo que reportó el usuario
          como *«el buscador quedó transparente… era cuando se abría»*.
          ⚠️ Es exactamente la misma trampa que ya está documentada en `Vecipp-Landing` con el
          desplegable «Productos», que hereda el remapeo por renderizarse dentro de su cabecera. Con el
          portal, el modal cuelga de `<body>` y ve los tokens de la página. De paso escapa de cualquier
          `overflow` o contexto de apilado de la barra. */}
      {abierto
        ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-foreground/40 p-4 pt-[10vh] backdrop-blur-sm"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cerrar();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Buscar en el Centro de ayuda"
            className="flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl"
            onKeyDown={alNavegar}
          >
            <div className="flex items-center gap-3 border-b px-4">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                ref={entrada}
                value={consulta}
                onChange={(e) => setConsulta(e.target.value)}
                placeholder="Buscar en las guías…"
                aria-label="Buscar en las guías"
                className="h-14 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
              />
              {cargando ? <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" /> : null}
              <button
                type="button"
                onClick={cerrar}
                aria-label="Cerrar el buscador"
                className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>

            <div ref={lista} className="scroll-fino min-h-0 flex-1 overflow-y-auto p-2">
              {q.length < 2 ? (
                <p className="px-3 py-8 text-center text-muted-foreground text-sm">
                  Escribe al menos dos letras. Busca en el texto completo de las {indice?.length ?? 107} guías.
                </p>
              ) : resultados.length === 0 ? (
                <p className="px-3 py-8 text-center text-muted-foreground text-sm">
                  No encontramos nada para «{consulta.trim()}». Prueba con otra palabra, o escríbenos a{" "}
                  <a href="mailto:soporte@vecipp.com" className="font-medium text-primary hover:underline">
                    soporte@vecipp.com
                  </a>
                  .
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {resultados.map((r, i) => (
                    <li key={`${r.entrada.m}/${r.entrada.s}`}>
                      <button
                        type="button"
                        data-resultado
                        onMouseEnter={() => setActivo(i)}
                        onClick={() => ir(r)}
                        className={cn(
                          "flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                          i === activo ? "bg-primary/10" : "hover:bg-muted",
                        )}
                      >
                        <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-sm">
                            <Resaltado texto={r.entrada.t} consulta={q} />
                          </span>
                          <span className="mt-0.5 block text-muted-foreground text-xs leading-relaxed">
                            <Resaltado texto={r.fragmento} consulta={q} />
                          </span>
                          <span className="mt-1 block text-[0.7rem] text-muted-foreground uppercase tracking-wide">
                            {NOMBRE_MODULO.get(r.entrada.m) ?? r.entrada.m}
                          </span>
                        </span>
                        {i === activo ? (
                          <CornerDownLeft className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="hidden items-center gap-4 border-t px-4 py-2.5 text-[0.7rem] text-muted-foreground sm:flex">
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border px-1 py-0.5">↵</kbd> abrir
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border px-1 py-0.5">↑</kbd>
                <kbd className="rounded border px-1 py-0.5">↓</kbd> moverse
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border px-1 py-0.5">esc</kbd> cerrar
              </span>
              {resultados.length ? <span className="ml-auto">{resultados.length} resultados</span> : null}
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}
    </>
  );
}
